// ============================================================================
// The window every stock entry opens in — the same shape as the building
// page's card window (PLAYBOOK §15b): a dialog on a desktop, a bottom sheet with
// a PINNED footer under 640 px, one Cancel and one Save.
//
// ⛔ A failed save keeps the window open with the typed values in it and shows
// the sentence inside the window (TCSD-0044). The caller owns the draft and
// resets it every time the window opens and after a successful save (§15a).
// ============================================================================

import { ReactNode, useEffect, useState } from 'react';
import { WriteDidNotLandError } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';

export function saveErrorText(e: unknown): string {
  if (e instanceof WriteDidNotLandError) return e.described.message;
  return errorMessage(e, 'That did not save.');
}

export interface StockWindowProps {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  /** Readiness is decided here, before any round trip (§15a). */
  canSave: boolean;
  saveLabel?: string;
  onCancel: () => void;
  /** Throw to keep the window open; resolve to close it. */
  onSave: () => Promise<void>;
  /** A second action beside Save, e.g. "Return to automatic". */
  secondary?: { label: string; onClick: () => Promise<void> };
  children: ReactNode;
}

export function StockWindow({
  open,
  title,
  subtitle,
  canSave,
  saveLabel = 'Save',
  onCancel,
  onSave,
  secondary,
  children,
}: StockWindowProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, saving, onCancel]);

  if (!open) return null;

  async function run(fn: () => Promise<void>) {
    setSaving(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(saveErrorText(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-5"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[94vh] w-full flex-col rounded-t-2xl border border-tcs-border bg-tcs-bg shadow-2xl sm:max-h-[90vh] sm:max-w-xl sm:rounded-2xl"
      >
        <header className="border-b border-tcs-border px-5 py-4">
          <h3 className="text-base font-semibold text-tcs-text">{title}</h3>
          {subtitle && <div className="mt-1 text-xs text-tcs-text-muted">{subtitle}</div>}
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div
              role="alert"
              className="rounded border border-st-danger-border-strong bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong"
            >
              {error}
            </div>
          )}
          {children}
        </div>
        <footer className="sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-tcs-border bg-tcs-bg px-5 py-3 sm:rounded-b-2xl">
          {secondary && (
            <button
              type="button"
              disabled={saving}
              onClick={() => run(secondary.onClick)}
              className="mr-auto rounded border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
            >
              {secondary.label}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs text-tcs-text hover:bg-tcs-surface disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => run(onSave)}
            disabled={!canSave || saving}
            className="rounded border border-tcs-accent bg-tcs-accent-ink px-3 py-1.5 text-xs font-medium text-tcs-accent-on hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** A labelled number field with − / + steppers. Empty means "not entered", never 0. */
export function QtyField({
  label,
  value,
  onChange,
  step,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step: number;
  hint?: string;
}) {
  const n = value === '' ? null : Number(value);
  const bump = (d: number) => {
    const next = Math.max(0, (n ?? 0) + d);
    onChange(String(Math.round(next * 100) / 100));
  };
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-tcs-text-muted">{label}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Less ${label}`}
          onClick={() => bump(-step)}
          className="h-9 w-9 rounded border border-tcs-border bg-tcs-raised text-tcs-text"
        >
          −
        </button>
        <input
          inputMode="decimal"
          className="h-9 w-24 rounded border border-tcs-border bg-tcs-field px-2 text-center text-base text-tcs-text"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
        />
        <button
          type="button"
          aria-label={`More ${label}`}
          onClick={() => bump(step)}
          className="h-9 w-9 rounded border border-tcs-border bg-tcs-raised text-tcs-text"
        >
          +
        </button>
      </div>
      {hint && <span className="mt-1 block text-[11px] text-tcs-text-muted">{hint}</span>}
    </label>
  );
}

export const FIELD_CLASS =
  'w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-base text-tcs-text sm:text-sm';

export function parseQty(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
